// Demo accounts, on the Admin page (byj-1wv.84.1): the people granted the
// relay's demo add-on, who are shown Share's demo when they sign in — every
// page answered in the browser from invented data (src/demo) — never their
// own. An admin makes an address a demo account, until a day or with no end,
// and makes it an ordinary account again, asked first. As the Admins tab
// lists its delegates: the grants as rows under their headings, Revoke
// behind ⋯, and making one a short form over the page opened beside the
// tab's lead, the person found as they are typed (components/PersonField).
import { useCallback, useEffect, useState } from "react";
import { isAddress } from "../address";
import { DemoAddon } from "../demo/flag";
import { api, type AdminUser, type Grant } from "../api";
import { t, whenMinute } from "../i18n";
import { dayFromToday, endOfDay } from "./Admins";
import { useConfirm } from "./Confirm";
import { FormDialog } from "./FormDialog";
import { PersonField } from "./PersonField";
import { RowMenu, type MenuItem } from "./RowMenu";
import { EmptyRows, Grouped, initialOf } from "./Rows";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** The calls the section makes, so a test can supply its own. */
export type DemoAccountsApi = Pick<typeof api, "adminGrants" | "grant" | "revoke" | "adminUsers">;

/** A demo account's row: until when, by whom, whether it holds; Revoke behind ⋯. */
function DemoRow({ g, onRevoke }: { g: Grant; onRevoke: (g: Grant) => void }) {
  const sub = [
    g.name,
    g.expires_at ? t("admin:adminUntil", { w: whenMinute(g.expires_at) }) : t("admin:adminNoEnd"),
    g.granted_by && t("admin:adminBy", { email: g.granted_by }),
  ].filter(Boolean);
  const menu: MenuItem[] = [{ label: t("admin:adminRevoke"), onSelect: () => onRevoke(g), danger: true }];
  return (
    <li className={`share-row admin-row ${g.live ? "is-live" : "is-ended"}`} data-testid="demo-account">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {initialOf(g.email)}
      </span>
      <div className="row-main">
        <span className="row-title">{g.email}</span>
        <div className="row-sub">{sub.join(" · ")}</div>
      </div>
      <div className="row-meta">{!g.live && <span className="chip chip-warn">{t("admin:adminExpired")}</span>}</div>
      <RowMenu label={t("shares:moreFor", { title: g.email })} items={menu} />
    </li>
  );
}

/**
 * Making an address a demo account, a short form over the page
 * (components/FormDialog): the person found as they are typed, or an address
 * nobody has used yet, and a last day if any. The dialog picks; the tab
 * grants. One who has signed in before is said to lose their own work to the
 * demo while it lasts (byj-1wv.106): the relay answers a demo account nothing
 * of its own (byj-1wv.84.7).
 */
function DemoDialog({ find, onSubmit, onClose }: {
  find: DemoAccountsApi["adminUsers"];
  onSubmit: (email: string, until?: string) => Promise<void>;
  onClose: () => void;
}) {
  const [email, setEmail] = useState("");
  const [day, setDay] = useState("");
  // who the address is, picked or found typed whole (PersonField's onPerson)
  const [who, setWho] = useState<AdminUser | null>(null);
  // someone using Share: not one only a grant named, nor an admin, who still sees the real app,
  // nor already a demo account
  const usesShare = !!who && who.signed_in === true && !who.admin && !who.demo?.live;
  return (
    <FormDialog
      id="demo-account-dialog"
      testid="demo-account-form"
      title={t("admin:demoAccountsMake")}
      submitLabel={t("admin:demoAccountsMake")}
      canSubmit={isAddress(email)}
      onSubmit={() => onSubmit(email.trim(), endOfDay(day))}
      onClose={onClose}
    >
      <PersonField find={find} label={t("admin:adminEmail")} ariaLabel={t("admin:demoAccountsWho")} value={email} onChange={setEmail} onPick={(u) => setEmail(u.email)} onPerson={setWho} hint={t("admin:adminFindHintGrant")} addon="demo" />
      {usesShare && (
        <p className="warn small flush" role="status" data-testid="demo-account-signed-in">
          {t("admin:demoAccountsSignedIn", { email: who.email })}
        </p>
      )}
      <label className="field">
        {t("admin:adminExpires")}
        <input type="date" value={day} min={dayFromToday(0)} onChange={(e) => setDay(e.target.value)} aria-label={t("admin:adminExpires")} />
      </label>
      <p className="muted small flush">{t("admin:demoAccountsMakeNote")}</p>
    </FormDialog>
  );
}

export function DemoAccounts({ client = api, onCount }: { client?: DemoAccountsApi; onCount?: (n: number) => void }) {
  const [list, setList] = useState<Grant[] | null>(null);
  const [making, setMaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [ask, confirmDialog] = useConfirm();
  const reload = useCallback(async () => {
    try {
      const l = await client.adminGrants(DemoAddon);
      setList(l);
      onCount?.(l.length);
    } catch (err) {
      setError(errText(err));
    }
  }, [client, onCount]);
  useEffect(() => {
    void reload();
  }, [reload]);

  // made, said so; refused, said in the dialog, which stays open
  const make = async (email: string, until?: string) => {
    const g = await client.grant({ email, addon: DemoAddon, ...(until ? { expires_at: until } : {}) });
    setDone(t("admin:demoAccountsMade", { email: g.email }));
    setError(null);
    setMaking(false);
    await reload();
  };
  const revoke = async (g: Grant) => {
    if (!(await ask(t("admin:demoAccountsRevokeAsk", { email: g.email }), { action: t("admin:adminRevoke"), danger: true }))) return;
    try {
      await client.revoke(g.user_id, DemoAddon);
      setDone(t("admin:demoAccountsRevoked", { email: g.email }));
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
    await reload();
  };

  return (
    <div data-testid="demo-accounts">
      {/* the tab's one way to add, beside what the tab is */}
      <div className="panel-head">
        <p className="muted small panel-lead">{t("admin:demoAccountsLead")}</p>
        <button
          className="primary"
          aria-haspopup="dialog"
          aria-expanded={making}
          aria-controls={making ? "demo-account-dialog" : undefined}
          onClick={() => {
            setDone(null);
            setMaking(true);
          }}
        >
          {t("admin:demoAccountsMake")}
        </button>
      </div>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {done && (
        <p className="ok-text" role="status">
          {done}
        </p>
      )}
      {list === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : list.length === 0 ? (
        <EmptyRows text={t("admin:demoAccountsNone")} />
      ) : (
        <Grouped
          id="demo-accounts"
          groups={[
            { title: t("admin:adminGrantsLive"), items: list.filter((g) => g.live), none: <EmptyRows text={t("admin:adminNoneLive")} /> },
            { title: t("admin:adminGrantsEnded"), items: list.filter((g) => !g.live) },
          ]}
          row={(g) => <DemoRow key={g.user_id} g={g} onRevoke={(x) => void revoke(x)} />}
        />
      )}
      {making && <DemoDialog find={client.adminUsers} onSubmit={make} onClose={() => setMaking(false)} />}
      {confirmDialog}
    </div>
  );
}
